import { PaymentServiceStore } from '@/app/stores/PaymentServiceStore/PaymentServiceStore'
import { POSStore } from '@/app/stores/POSStore'
import { action, computed, IReactionDisposer, observable, reaction, when } from 'mobx'
import { Dayjs } from 'dayjs'
import { dayjs } from '../utilities/dayjs'
import { LocalStorage } from '../utilities/LocalStorage'
import { productApi, ProductModels } from '@getgreenline/products'
import { PAYMENT_FEE } from '../constants/general'
import {
  CreateTransaction,
  ICreatePaymentLine,
  ICreateTransaction,
  INestedProduct,
  ITransaction,
  PaymentLineType,
} from '@getgreenline/homi-shared'
import {
  BlazepayEnums,
  BlazepayFeeModels,
  BlazepayModels,
  BlazepayPaymentModels,
} from '@getgreenline/payments'

/**
 * Used to operate tasks with multiple Mobx stores
 */
export class PaymentFeeStore {
  readonly FEE_TTL = 5 * 60 * 1000 // 5 minutes

  /**
   * eg) blc-10-12-payment-fees
   */
  paymentFeeStorageKey: string

  lastFetchedPaymentFeeTime?: Dayjs

  numPaymentLines = { prev: 0, curr: 0 }

  reactToCartItemDisposer: IReactionDisposer | undefined = undefined

  /**
   * @key fee.name: used to map by IFeeResponseContractAttribute
   * @value INestedProduct
   */
  @observable private mappedFeeProducts = observable.map<string, INestedProduct>()

  constructor(private posStore: POSStore, private paymentServiceStore: PaymentServiceStore) {
    this.paymentFeeStorageKey = `${paymentServiceStore.merchantId}-payment-fees`
    this.setReactionToCartItemDisposer()
  }

  private waitForProductsAndFeeConfig = async ({
    areListingsReady,
    isSystemFeeCategoryExist,
    isConfigLoading,
  }: {
    areListingsReady: boolean
    isSystemFeeCategoryExist: boolean
    isConfigLoading: boolean
  }) => {
    const timeout = 10 * 60 * 1000 // 10 minutes

    return when(() => areListingsReady && isSystemFeeCategoryExist && !isConfigLoading, {
      timeout,
    }).catch(() => {
      throw new Error('Failed to load payment fees')
    })
  }

  @action
  setFeeProduct = (feeProduct: INestedProduct) => {
    this.mappedFeeProducts.set(feeProduct.name || PAYMENT_FEE, feeProduct)
  }

  private fetchAndStoreFeesInStorage = async () => {
    // Fetch recent configs
    await this.paymentServiceStore.paymentOption.config.keepDataUpToDate()

    // Store fees
    const paymentFees = [...this.paymentServiceStore.paymentOption.config.fees.values()]
    LocalStorage.setPaymentFees(this.paymentFeeStorageKey, paymentFees)

    // Update lastFetchedFeeTime
    this.lastFetchedPaymentFeeTime = dayjs()

    return paymentFees
  }

  private createPaymentFeeProduct = async (
    paymentFees: BlazepayModels.IIncluded<BlazepayFeeModels.IFeeResponseContractAttribute>[],
  ) => {
    const feeNames = paymentFees.map((fee) => fee.attributes.name)

    const companyId = this.posStore.company.id
    const promises = feeNames.map((name) =>
      productApi.createPaymentFeeProduct(companyId, { feeName: name || PAYMENT_FEE }),
    )

    const paymentFeeProducts = await Promise.all(promises)

    paymentFeeProducts.forEach((feeProduct) => {
      this.setFeeProduct(feeProduct)
    })

    return this.posStore.setPOSProducts()
  }

  private setReactionToCartItemDisposer = () => {
    this.reactToCartItemDisposer = reaction(
      () => ({
        currentPayment: this.posStore.currentPayment,
        paymentLines: this.posStore.currentPayment.paymentLines,
        areListingsReady: this.posStore.areListingsReady,
        systemFeeCategory: this.posStore.productStore.systemFeeCategory,
        isConfigLoading: this.paymentServiceStore.paymentOption.config.isLoading,
      }),
      async ({ areListingsReady, paymentLines, systemFeeCategory, isConfigLoading }) => {
        try {
          // Update numPaymentLines
          this.numPaymentLines.prev = this.numPaymentLines.curr
          this.numPaymentLines.curr = paymentLines.length

          await this.waitForProductsAndFeeConfig({
            areListingsReady,
            isSystemFeeCategoryExist: Boolean(systemFeeCategory),
            isConfigLoading,
          })

          const isFirstProductAdded =
            this.numPaymentLines.prev === 0 && this.numPaymentLines.curr === 1

          const hasStoredPaymentFees = LocalStorage.getPaymentFees(this.paymentFeeStorageKey)

          const currentTime = dayjs()
          const differences = currentTime.diff(this.lastFetchedPaymentFeeTime)

          if (
            this.lastFetchedPaymentFeeTime === undefined ||
            (isFirstProductAdded && (!hasStoredPaymentFees || differences > this.FEE_TTL))
          ) {
            const fees = await this.fetchAndStoreFeesInStorage()

            await this.createPaymentFeeProduct(fees)
          }
        } catch (error) {
          // if error fallbacks to localstorage values
          const paymentFees = LocalStorage.getPaymentFees(this.paymentFeeStorageKey)

          if (paymentFees) {
            this.paymentServiceStore.paymentOption.config.mapFees(paymentFees)
          }
        }
      },
      {
        // this ensures to run when the class initiates
        fireImmediately: true,
      },
    )
  }

  private getFeeByProvider(
    provider: BlazepayEnums.Provider,
  ): BlazepayModels.IIncluded<BlazepayFeeModels.IFeeResponseContractAttribute> | undefined {
    const config = this.paymentServiceStore.paymentOption.config.configs.get(provider)
    const feeRelationships = config?.relationships?.fees

    if (!feeRelationships) {
      return
    }

    let deserializedFeeRelationships: BlazepayModels.IRelationship[]

    // FIXIT: config.relationships.fees type is incorrect
    if ('data' in feeRelationships) {
      deserializedFeeRelationships = feeRelationships.data as BlazepayModels.IRelationship[]
    } else {
      if (Array.isArray(feeRelationships)) {
        deserializedFeeRelationships = feeRelationships.map(
          (item) => item?.data as BlazepayModels.IRelationship,
        )
      } else {
        deserializedFeeRelationships = [(feeRelationships as BlazepayModels.IRelationshipData).data]
      }
    }

    const matchingFee = deserializedFeeRelationships.find((feeRelation) =>
      this.paymentServiceStore.paymentOption.config.fees.has(feeRelation.id),
    )

    if (matchingFee) {
      return this.paymentServiceStore.paymentOption.config.fees.get(matchingFee.id)
    }
  }

  private getFeeProductByName(paymentFeeName: string) {
    return this.mappedFeeProducts.get(paymentFeeName)
  }

  getFeeByPaymentTypeId(
    transaction: CreateTransaction | ICreateTransaction | ITransaction,
  ): BlazepayModels.IIncluded<BlazepayFeeModels.IFeeResponseContractAttribute> | undefined {
    const provider = BlazepayPaymentModels.getProvider(transaction.paymentTypeId)

    if (!provider) {
      return
    }

    return this.getFeeByProvider(provider)
  }

  // YK_TODO: When refunding -> Should exclude payment fee products like tip
  getPaymentProductAndAmount(transaction: CreateTransaction | ICreateTransaction | ITransaction) {
    const paymentFee = this.getFeeByPaymentTypeId(transaction)

    if (!paymentFee) {
      return
    }

    const fee = BlazepayPaymentModels.getPaymentFeeNameAndAmount(paymentFee, transaction)
    const paymentFeeProduct = this.getFeeProductByName(paymentFee.attributes.name)
    const paymentLine: ICreatePaymentLine | undefined = paymentFeeProduct
      ? {
          type: PaymentLineType.PRODUCT,
          sku: paymentFeeProduct?.sku || null,
          productId: paymentFeeProduct?.id || null,
          productName: paymentFeeProduct?.name || fee.name || PAYMENT_FEE,
          units: 1,
          unit: 'unit',
          inventorySubtracted: 1,
          price: fee.amount,
          cartDiscountPrice: 0,
          depositFee: 0,
          discountPrice: 0,
          barcode: null,
          batchId: null,
          discountId: null,
          discountMessage: null,
          shortDescription: ProductModels.ProductType.PAYMENT_FEE,
        }
      : undefined

    return {
      ...fee,
      paymentLine: paymentLine,
    }
  }

  @computed
  get feeProducts(): INestedProduct[] {
    return [...this.mappedFeeProducts.values()]
  }
}
